import axios from "axios";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { app } from "../firebase/config";
import { ADMIN_PASSWORD_STORAGE_KEY } from "../utils/adminApi";

const API_BASE_URL = process.env.REACT_APP_API_URL;

function waitForAuthenticatedUser(timeoutMs = 3000) {
  const auth = getAuth(app);

  if (auth.currentUser) {
    return Promise.resolve(auth.currentUser);
  }

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      unsubscribe();
      reject(
        new Error(
          "Lead Engine admin yêu cầu đăng nhập bằng Google/Firebase admin trước khi gọi API."
        )
      );
    }, timeoutMs);

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        return;
      }

      clearTimeout(timeoutId);
      unsubscribe();
      resolve(user);
    });
  });
}

async function getAdminAuthHeaders() {
  const storedAdminPassword = localStorage.getItem(ADMIN_PASSWORD_STORAGE_KEY);
  const adminPassword =
    storedAdminPassword ||
    process.env.REACT_APP_ADMIN_PASSWORD ||
    process.env.ADMIN_PASSWORD;

  if (localStorage.getItem("adminAuthenticated") === "true" && adminPassword) {
    return {
      "X-Admin-Password": adminPassword,
    };
  }

  const auth = getAuth(app);
  const user = auth.currentUser || (await waitForAuthenticatedUser());

  if (!user) {
    throw new Error(
      "Lead Engine admin yêu cầu đăng nhập bằng Google/Firebase admin trước khi gọi API."
    );
  }

  const token = await user.getIdToken();
  return {
    Authorization: `Bearer ${token}`,
  };
}

async function adminRequest(config) {
  const headers = await getAdminAuthHeaders();
  return axios({
    ...config,
    headers: {
      ...(config.headers || {}),
      ...headers,
    },
  });
}

export async function fetchCommentOverviewStats() {
  const response = await adminRequest({
    method: "get",
    url: `${API_BASE_URL}/api/admin/comments/stats`,
  });
  return response.data?.data || {};
}

export async function fetchCommentUsers() {
  const response = await adminRequest({
    method: "get",
    url: `${API_BASE_URL}/api/admin/comments/users`,
  });
  return response.data?.data || [];
}

export async function fetchCommentUserDetail(userId) {
  const response = await adminRequest({
    method: "get",
    url: `${API_BASE_URL}/api/admin/comments/users/${userId}/detail`,
  });
  return response.data?.data || {};
}

export async function pauseAllUserCommentConfigs(userId) {
  const response = await adminRequest({
    method: "post",
    url: `${API_BASE_URL}/api/admin/comments/users/${userId}/pause-all`,
  });
  return response.data?.data;
}

export async function fetchCommentQueue({ status, limit } = {}) {
  const response = await adminRequest({
    method: "get",
    url: `${API_BASE_URL}/api/admin/comments/queue`,
    params: {
      status: status || undefined,
      limit: limit || 50,
    },
  });
  return response.data?.data || [];
}

export async function flushStaleQueueItems() {
  const response = await adminRequest({
    method: "post",
    url: `${API_BASE_URL}/api/admin/comments/queue/flush-stale`,
  });
  return response.data?.data;
}

export async function purgeCommentQueue() {
  const response = await adminRequest({
    method: "post",
    url: `${API_BASE_URL}/api/admin/comments/queue/purge`,
  });
  return response.data?.data;
}

export async function fetchCommentEvents(limit = 50) {
  const response = await adminRequest({
    method: "get",
    url: `${API_BASE_URL}/api/admin/comments/events`,
    params: { limit },
  });
  return response.data?.data || [];
}

export async function fetchCommentMetrics({ from, to } = {}) {
  const response = await adminRequest({
    method: "get",
    url: `${API_BASE_URL}/api/admin/comments/metrics`,
    params: {
      from: from || undefined,
      to: to || undefined,
    },
  });
  return response.data?.data || [];
}

export async function fetchCommentMetricsPerUser({ from, to } = {}) {
  const response = await adminRequest({
    method: "get",
    url: `${API_BASE_URL}/api/admin/comments/metrics/users`,
    params: {
      from: from || undefined,
      to: to || undefined,
    },
  });
  return response.data?.data || [];
}

export async function fetchCommentOperations(limit = 50) {
  const response = await adminRequest({
    method: "get",
    url: `${API_BASE_URL}/api/admin/comments/operations`,
    params: { limit },
  });
  return response.data?.data || {};
}

export async function adminApproveReview({
  userId,
  configId,
  reviewId,
  replyText,
}) {
  const response = await adminRequest({
    method: "post",
    url: `${API_BASE_URL}/api/admin/comments/reviews/${reviewId}/approve`,
    data: {
      userId,
      configId,
      replyText,
    },
  });
  return response.data?.data;
}

export async function adminRejectReview({ userId, configId, reviewId, reason }) {
  const response = await adminRequest({
    method: "post",
    url: `${API_BASE_URL}/api/admin/comments/reviews/${reviewId}/reject`,
    data: {
      userId,
      configId,
      reason,
    },
  });
  return response.data?.data;
}
